import { ChangeDetectionStrategy, ChangeDetectorRef, Component, input, signal, inject, DestroyRef, computed, effect } from '@angular/core';

import { PipesModule } from '../../pipes/pipes.module';
import { SpinnerComponent } from './spinner.component';
import type { EffectCleanupRegisterFn, InputSignal, Signal, WritableSignal } from '@angular/core';

export type LoadingIndicatorPosition = 'center' | 'top' | 'inline';

@Component({
  selector: 'td-wb-loading-indicator',
  templateUrl: 'loading-indicator.component.html',
  styleUrls: ['loading-indicator.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SpinnerComponent, PipesModule],

  host: {
    '[class.td-wb-loading-spinner--inline]': 'isInline()',
    '[class.td-wb-loading-spinner--center]': 'isCenter()',
    '[class.td-wb-loading-spinner--top]': 'isTop()',
    '[attr.aria-live]': 'ariaLive()'
  }
})
export class LoadingIndicatorComponent {
  private static readonly DISPLAY_DELAY_MS: number = 300;
  private static readonly REMOVE_DELAY_MS: number = 12000;

  readonly position: InputSignal<LoadingIndicatorPosition | undefined> = input<LoadingIndicatorPosition | undefined>(undefined);
  readonly displayDelay: InputSignal<number | undefined> = input<number | undefined>(LoadingIndicatorComponent.DISPLAY_DELAY_MS);
  readonly removeDelay: InputSignal<number | undefined> = input<number | undefined>(LoadingIndicatorComponent.REMOVE_DELAY_MS);
  readonly ariaLive: InputSignal<string> = input<string>('polite');

  readonly displaySpinner: WritableSignal<boolean> = signal<boolean>(false);

  private displaySpinnerTimer: ReturnType<typeof setTimeout> | undefined;
  private removeSpinnerTimer: ReturnType<typeof setTimeout> | undefined;

  isCenter: Signal<boolean> = computed((): boolean => this.position() === 'center');
  isTop: Signal<boolean> = computed((): boolean => this.position() === 'top');
  isInline: Signal<boolean> = computed((): boolean => {
    const pos = this.position();
    return pos === undefined || pos === 'inline';
  });

  private readonly effectiveDisplayDelay: Signal<number> = computed((): number => {
    return this.displayDelay() ?? LoadingIndicatorComponent.DISPLAY_DELAY_MS;
  });

  readonly effectiveRemoveDelay: Signal<number> = computed((): number => {
    return this.removeDelay() ?? LoadingIndicatorComponent.REMOVE_DELAY_MS;
  });

  private readonly cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  constructor() {
    this.destroyRef.onDestroy((): void => {
      this.clearTimers();
    });

    effect((onCleanup: EffectCleanupRegisterFn): void => {
      this.clearTimers();
      this.displaySpinner.set(false);

      const displayDelay = this.effectiveDisplayDelay();
      const removeDelay = this.effectiveRemoveDelay();

      if (displayDelay !== 0) {
        this.displaySpinnerTimer = setTimeout((): void => {
          this.displaySpinnerTimer = undefined;
          this.showSpinner(removeDelay);
        }, displayDelay);
      } else {
        this.showSpinner(removeDelay);
      }

      onCleanup((): void => {
        this.clearTimers();
      });
    });
  }

  private clearTimers(): void {
    if (this.displaySpinnerTimer !== undefined) {
      clearTimeout(this.displaySpinnerTimer);
      this.displaySpinnerTimer = undefined;
    }
    if (this.removeSpinnerTimer !== undefined) {
      clearTimeout(this.removeSpinnerTimer);
      this.removeSpinnerTimer = undefined;
    }
  }

  private showSpinner(removeDelay: number): void {
    this.displaySpinner.set(true);
    this.cdr.markForCheck();
    this.removeSpinnerTimer = setTimeout((): void => {
      this.displaySpinner.set(false);
      this.removeSpinnerTimer = undefined;
      this.cdr.markForCheck();
    }, removeDelay);
  }

}
